/**
 * useSharedPipeline — URL Hash Parser Hook
 *
 * On mount, checks `window.location.hash` for:
 * 1. `#p=` — our native shared pipeline format
 * 2. `#recipe=` — CyberChef recipe URL format
 *
 * If found, deserializes the payload, loads the pipeline into the store,
 * and navigates to the editor page.
 *
 * @see docs/design/pipeline-sharing.md §S.3
 */

import LZString from 'lz-string';
import { useEffect, useRef } from 'react';

import i18n from '@/i18n/config';
import { isCyberChefUrl, parseCyberChefUrl } from '@/lib/cyberchefImport';
import {
  type DeserializedPipeline,
  deserializePipeline,
  hydratePipelineSteps,
  parseShareUrl,
} from '@/lib/pipelineSharing';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { useTabStore } from '@/store/useTabStore';
import { toast } from '@/store/useToastStore';

/**
 * Call this hook once at the app root (after tool registration).
 * It is idempotent — it only processes the hash on the first matching mount.
 */
export function useSharedPipeline(): void {
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;

    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!hash) return;

    // --- 1. Try our native format first (#p=...) ---
    const compressed = parseShareUrl();
    if (compressed) {
      hasProcessed.current = true;
      handleNativePipeline(compressed);
      return;
    }

    // --- 2. Try CyberChef format (#recipe=...) ---
    if (isCyberChefUrl(hash)) {
      hasProcessed.current = true;
      handleCyberChefImport(hash);
      return;
    }
  }, []);
}

// ---------------------------------------------------------------------------
// Native pipeline handler (existing logic)
// ---------------------------------------------------------------------------

function handleNativePipeline(compressed: string): void {
  const data = deserializePipeline(compressed);

  cleanHash();

  if (!data) {
    console.error('[useSharedPipeline] Failed to load shared pipeline — the link may be malformed or corrupted.');
    toast(i18n.t('errors:sharing.invalidPipelineLink'), 'error');
    return;
  }

  loadPipelineIntoStore(data);

  const stepCount = data.steps.length;
  const nameStr = data.name ? ` "${data.name}"` : '';
  console.info(`[useSharedPipeline] Loaded shared pipeline${nameStr} with ${stepCount} step${stepCount !== 1 ? 's' : ''}.`);

  // Show toast — warn if some tools were skipped
  const originalStepCount = (JSON.parse(
    LZString.decompressFromEncodedURIComponent(compressed) ?? '{}',
  ) as { s?: unknown[] }).s?.length ?? stepCount;

  if (stepCount < originalStepCount) {
    toast(i18n.t('errors:sharing.toolsNotAvailable'), 'warning', 5000);
  } else {
    toast(i18n.t('errors:sharing.pipelineLoaded'), 'success');
  }
}

// ---------------------------------------------------------------------------
// CyberChef import handler
// ---------------------------------------------------------------------------

function handleCyberChefImport(hash: string): void {
  const result = parseCyberChefUrl(hash);

  cleanHash();

  if (!result || result.pipeline.steps.length === 0) {
    console.error('[useSharedPipeline] Failed to parse CyberChef URL — no mappable operations found.');
    toast(i18n.t('errors:sharing.cyberChefNoMatch'), 'error');
    return;
  }

  loadPipelineIntoStore(result.pipeline);

  const { mappedCount, totalOperations, skippedOperations } = result;
  console.info(
    `[useSharedPipeline] Imported CyberChef recipe: ${mappedCount}/${totalOperations} operations mapped.` +
    (skippedOperations.length > 0 ? ` Skipped: ${skippedOperations.join(', ')}` : ''),
  );

  if (skippedOperations.length > 0) {
    const skippedStr = skippedOperations.length <= 3
      ? skippedOperations.join(', ')
      : `${skippedOperations.slice(0, 3).join(', ')} + ${skippedOperations.length - 3} more`;
    toast(
      i18n.t('errors:sharing.cyberChefPartial', { mapped: mappedCount, total: totalOperations, skipped: skippedStr }),
      'warning',
      6000,
    );
  } else {
    toast(i18n.t('errors:sharing.cyberChefSuccess', { count: mappedCount }), 'success');
  }
}

// ---------------------------------------------------------------------------
// Shared: load pipeline into store + navigate
// ---------------------------------------------------------------------------

function loadPipelineIntoStore(data: DeserializedPipeline): void {
  const hydratedSteps = hydratePipelineSteps(data);
  const tabName = data.name ?? 'Shared Pipeline';

  // Create a new tab pre-loaded with the shared pipeline data
  const tabStore = useTabStore.getState();
  const newTabId = tabStore.createTab(tabName);

  // Write pipeline data directly into the new tab.
  // Set status to 'active' so EditorPage doesn't treat it as a blank
  // "selecting" tab (which would open the tools drawer and delete the
  // tab when the drawer closes).
  useTabStore.setState((state) => ({
    tabs: state.tabs.map((t) =>
      t.id === newTabId
        ? {
            ...t,
            pipeline: hydratedSteps,
            input: data.inputData ?? '',
            output: '',
            inputType: null,
            stepResults: [],
            pipelineVariables: data.variables ?? {},
            activePipelineName: data.name ?? null,
            activePipelineDescription: null,
            isProcessing: false,
            status: 'active' as const,
          }
        : t
    ),
  }));

  // Also set appStore so EditorPage hydration picks up the right state
  useEditorStore.setState({
    pipeline: hydratedSteps,
    activePipelineName: data.name ?? null,
    activePipelineDescription: null,
    activePipelineNameIsAuto: !data.name,
    activePipelineDescriptionIsAuto: true,
    activePipelineId: null,
    pipelineVariables: data.variables ?? {},
    output: '',
    outputType: null,
    input: data.inputData ?? '',
  });

  useExecutionStore.setState((state) => ({
    errors: {},
    error: null,
    stepResults: [],
    isProcessing: false,
    stepThrough: null,
    debugSnapshot: null,
    isStepThrough: false,
    currentStepIndex: null,
    rerunFromStepIndex: null,
    suppressAutoRun: false,
    executionRevision: state.executionRevision + 1,
  }));

  // Navigate to editor if not already there
  if (window.location.pathname !== '/' && window.location.pathname !== '/test/') {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

/** Remove the hash from the URL without triggering navigation. */
function cleanHash(): void {
  const url = window.location.pathname + window.location.search;
  window.history.replaceState(null, '', url);
}
